import React from 'react';

import { Icon, Pressable } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import loyaltyPointsIcon from 'components/icons/loyalty-points-icon';
import Link from 'components/link';
import { ProgressMeter } from 'components/progress-meter';
import { Box, BoxProps, Typography, TypographyProps, withTypography } from 'components/ui';
import { useIsDesktopLargeBreakpoint } from 'hooks/use-media-query';
import { getDesignToken, primitive, spacing } from 'styles/constants';

const CATEGORY_HEADER_HEIGHT = 44;
const METER_CONTAINER_WIDTH = 375;
const EXPIRING_POINTS_MAX_WIDTH = 370;

type ChallengeContainerProps = BoxProps & {
  addMargin?: boolean;
};

type RewardsTabLinkTextProps = TypographyProps & {
  active?: boolean;
};

type IconContainerProps = BoxProps & {
  ml?: '$2';
};

const styles = StyleSheet.create({
  categoryLabel: {
    flexGrow: 2,
    marginBottom: 0,
  },
  challengeContainerWithMargin: {
    margin: spacing.$4,
  },
  loyaltyRewardsListContainer: {
    marginTop: spacing.$2,
  },
  rewardCategoryHeader: {
    alignContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    height: CATEGORY_HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: spacing.$4,
  },
  rewardCategoryCarouselContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: spacing.$4,
  },
  rewardCategoryMeterContainer: {
    alignItems: 'center',
    backgroundColor: primitive.$blackOpacity4,
    borderTopColor: primitive.$greyFour,
    borderTopWidth: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: spacing.$4,
    paddingBottom: spacing.$4,
    paddingTop: spacing.$8,
  },
  meterContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: spacing.$8,
    width: METER_CONTAINER_WIDTH,
  },
  pointsNeededContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  rewardsTabLinkText: {
    marginBottom: spacing.$1,
    marginTop: spacing.$1,
  },
  rewardsTabsBorderBottom: {
    backgroundColor: primitive.$blackOpacity10,
    bottom: 0,
    height: 0.5,
    position: 'absolute',
    width: '100%',
  },
  expiringPointsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.$2,
    marginHorizontal: spacing.$2,
  },
  iconContainer: {
    alignItems: 'center',
    backgroundColor: primitive.$meltyYellow,
    borderRadius: 24,
    flexShrink: 0,
    height: spacing.$8,
    justifyContent: 'center',
    marginRight: spacing.$3,
    width: spacing.$8,
  },
  iconContainerMarginLeft2: {
    marginLeft: spacing.$2,
  },
});

export const CategoryLabel = ({ style, ...props }: TypographyProps) => {
  const isDesktopLarge = useIsDesktopLargeBreakpoint();

  return React.createElement(Typography, {
    ...props,
    variant: 'headerOne',
    fontSize: 'xl',
    lineHeight: isDesktopLarge ? 36 : undefined,
    style: [styles.categoryLabel, style],
  });
};

export const ChallengeContainer = ({
  addMargin = true,
  style,
  ...props
}: ChallengeContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [addMargin && styles.challengeContainerWithMargin, style],
  });

export const LoyaltyRewardsListContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.loyaltyRewardsListContainer, style] });

export const LoyaltyRewardsListTitle = ({ style, ...props }: TypographyProps) => {
  const isDesktopLarge = useIsDesktopLargeBreakpoint();

  return React.createElement(Typography, {
    ...props,
    variant: 'headerOne',
    fontSize: isDesktopLarge ? 30 : undefined,
    lineHeight: isDesktopLarge ? 36 : undefined,
    style,
  });
};

export const RewardCategoryContainer = Pressable.withConfig<{
  locked: boolean;
}>(p => ({
  flexDir: 'column',
  paddingBottom: '$4',
  flex: 1,
  background: p.locked ? primitive.$blackOpacity4 : '',
}));

export const RewardCategoryHeader = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.rewardCategoryHeader, style] });

export const RewardCategoryCarouselContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.rewardCategoryCarouselContainer, style] });

export const RewardCategoryMeterContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.rewardCategoryMeterContainer, style] });

export const MeterContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.meterContainer, style] });

export const StyledProgressMeter = ({
  style,
  ...props
}: React.ComponentProps<typeof ProgressMeter>) =>
  React.createElement(ProgressMeter, {
    ...props,
    style: [{ backgroundColor: primitive.$white, borderWidth: 0 }, style],
  });

export const PointsNeededContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.pointsNeededContainer, style] });

export const PointsNeededText = withTypography({ fontSize: 'sm', bold: true, mr: '$1' });

export const PointsNeededDescription = withTypography({ fontSize: 'xs' });

export const StyledPointsIcon = loyaltyPointsIcon.withConfig({
  width: '$4',
  height: '$4',
  marginRight: '$1',
});

export const StyledRewardLock = Icon.withConfig({
  variant: 'locked',
  size: '$5',
});

export const PointsNeededLockIcon = StyledRewardLock.withConfig({
  size: '$5',
  color: primitive.$bbqBrown,
});

export const CrownIcon = loyaltyPointsIcon.withConfig({
  width: '$7',
  height: '$7',
  marginRight: '$1',
});

export const RewardsTabLink = Link.withConfig<{ active?: boolean }>(p => ({
  flexGrow: 1,
  isUnderlined: false,
  margin: 0,
  textTransform: 'none',
  borderBottomWidth: 3,
  justifyContent: 'center',
  alignItems: 'center',
  borderBottomColor: p.active ? getDesignToken('color-tertiary') : 'transparent',
}));

export const RewardsTabLinkText = ({ active, style, ...props }: RewardsTabLinkTextProps) => {
  const isDesktopLarge = useIsDesktopLargeBreakpoint();

  return React.createElement(Typography, {
    ...props,
    color: getDesignToken('color-tertiary'),
    fontFamily: active ? 'heading' : 'body',
    fontSize: 'md',
    lineHeight: isDesktopLarge ? 22 : undefined,
    style: [styles.rewardsTabLinkText, style],
  });
};

export const RewardsTabsBorderBottom = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.rewardsTabsBorderBottom, style] });

export const ExpiringPointsContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.expiringPointsContainer, style] });

export const ExpiringPoints = Link.withConfig({
  height: spacing.$13_5,
  maxWidth: {
    base: EXPIRING_POINTS_MAX_WIDTH,
    md: 'full',
  },
  marginTop: '$4',
  paddingX: {
    base: '$5',
    md: '$3',
  },
  backgroundColor: primitive.$white,
  borderRadius: 'lg',
  justifyContent: 'center',
  flexDirection: 'row',
  alignItems: 'center',
  _text: {
    textAlign: 'center',
  },
});

export const IconContainer = ({ ml, style, ...props }: IconContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.iconContainer, ml === '$2' && styles.iconContainerMarginLeft2, style],
  });

export const ExpiringIcon = loyaltyPointsIcon.withConfig({
  size: 5,
  fill: primitive.$white,
});
